import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, CheckCircle2, KeyRound, Lock } from "lucide-react";
import { useState } from "react";
import { privateCategoriesQuery } from "#/lib/exclusive";
import { notify } from "#/lib/notify";
import { cn } from "#/lib/utils";
import { redeemPrivateCode } from "#/server/functions/privateProducts";
import { useUnlockStore } from "#/store/unlock";
import { EyebrowPill, PillBtn } from "../components/primitives.js";
import { ROUTES } from "../lib/routes.js";

export const Route = createFileRoute("/private-products")({
  head: () => ({ meta: [{ title: "Private HP Products" }] }),
  component: PrivateProductsPage,
});

function PrivateProductsPage() {
  const unlockedSlugs = useUnlockStore((s) => s.unlockedSlugs);
  const applyRedeem = useUnlockStore((s) => s.applyRedeem);

  const { data: categories } = useQuery(privateCategoriesQuery);

  const [code, setCode] = useState("");

  const redeem = useMutation({
    mutationFn: (input: string) => redeemPrivateCode({ data: { code: input } }),
    onSuccess: (result) => {
      if (result.ok) {
        const before = new Set(unlockedSlugs);
        const newlyUnlocked = result.data.unlockedSlugs.filter(
          (s) => !before.has(s),
        );
        if (newlyUnlocked.length === 0) {
          notify.info("Already unlocked");
        } else if (newlyUnlocked.length === 1) {
          const label =
            (categories ?? []).find((c) => c.slug === newlyUnlocked[0])?.label ??
            result.data.label;
          notify.success("Unlocked", { description: label });
        } else {
          notify.success(`Unlocked ${newlyUnlocked.length} collections`);
        }
        applyRedeem({
          unlockedSlugs: result.data.unlockedSlugs,
          token: result.data.token,
          expiresInSeconds: result.data.expiresInSeconds,
        });
        setCode("");
      } else if (result.error === "invalid_code") {
        notify.error("Invalid password", {
          description: "That password didn't match anything.",
        });
      } else {
        notify.error("Couldn't redeem", {
          description: "Something went wrong. Please try again.",
        });
      }
    },
    onError: () => {
      notify.error("Couldn't redeem", {
        description: "Something went wrong. Please try again.",
      });
    },
  });

  const submit = () => {
    if (code.trim().length === 0) return;
    if (redeem.isPending) return;
    redeem.mutate(code.trim());
  };

  const total = categories?.length ?? 0;
  const unlockedCount = (categories ?? []).filter((c) =>
    unlockedSlugs.includes(c.slug),
  ).length;

  return (
    <div data-screen-label="Private HP Products" className="px-6 py-12 md:py-20">
      <div className="mx-auto max-w-6xl">
        <div className="grid items-start gap-10 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-7">
            <EyebrowPill variant="yellow" className="mb-5">
              HP families only
            </EyebrowPill>
            <h1 className="m-0 mb-4 font-bold font-display text-4xl text-lp-green-dk leading-tight md:text-5xl">
              Private HP products,
              <br />
              unlocked by password.
            </h1>
            <p className="m-0 mb-8 max-w-xl font-display text-base text-lp-text-lt leading-relaxed md:text-lg">
              Enter the password to unlock product/s reserved for Homeschool
              Plus (HP) families.
            </p>

            <div className="flex flex-col gap-3">
              {(categories ?? []).map((cat) => {
                const unlocked = unlockedSlugs.includes(cat.slug);
                return (
                  <div
                    key={cat.slug}
                    className={cn(
                      "flex items-start gap-4 rounded-2xl border bg-white/80 p-5 backdrop-blur transition",
                      unlocked
                        ? "border-lp-green ring-1 ring-lp-green/20"
                        : "border-lp-green-soft",
                    )}
                  >
                    <div
                      className={cn(
                        "flex size-11 shrink-0 items-center justify-center rounded-full",
                        unlocked
                          ? "bg-lp-green text-white"
                          : "bg-lp-grey text-lp-text-lt",
                      )}
                    >
                      {unlocked ? (
                        <CheckCircle2 className="size-5" />
                      ) : (
                        <Lock className="size-5" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="m-0 font-display font-semibold text-lg text-lp-green-dk">
                          {cat.label}
                        </h2>
                        <span
                          className={cn(
                            "rounded-full px-2 py-0.5 font-bold text-[10px] uppercase tracking-wider",
                            unlocked
                              ? "bg-lp-green/10 text-lp-green-dk"
                              : "bg-lp-grey text-lp-text-lt",
                          )}
                        >
                          {unlocked ? "Unlocked" : "Locked"}
                        </span>
                      </div>
                      <p className="m-0 mt-1 font-display text-lp-text-lt text-sm">
                        {cat.teaser}
                      </p>
                      {unlocked && (
                        <Link
                          to={ROUTES.shop}
                          search={{ category: [cat.slug], page: 1 }}
                          className="mt-2 inline-flex items-center gap-1 font-display font-semibold text-lp-green-dk text-sm hover:text-lp-green-mid"
                        >
                          Browse collection
                          <ArrowRight className="size-4" />
                        </Link>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:sticky lg:top-28 lg:col-span-5">
            <div className="rounded-[32px] border border-lp-green-soft bg-white p-7 shadow-card md:p-9">
              <div className="mb-5 flex justify-center">
                <div className="flex size-16 items-center justify-center rounded-full bg-lp-yellow/20">
                  <KeyRound className="size-7 text-lp-green-dk" />
                </div>
              </div>
              <h2 className="m-0 mb-2 text-center font-bold font-display text-2xl text-lp-green-dk md:text-3xl">
                Enter the password to unlock product/s
              </h2>
              <p className="m-0 mb-6 text-center font-display text-lp-text-lt text-sm">
                Passwords are case-insensitive.
              </p>
              <div className="flex flex-col gap-3">
                <input
                  value={code}
                  onChange={(e) => {
                    setCode(e.target.value);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submit();
                    }
                  }}
                  placeholder="Enter password"
                  autoCapitalize="characters"
                  className="h-11 w-full rounded-full border-2 border-lp-green bg-white px-5 text-center font-display text-base text-lp-text outline-none placeholder:text-lp-text-lt focus:border-lp-green-mid"
                />
                <PillBtn
                  size="sm"
                  variant="green"
                  onClick={submit}
                  disabled={code.trim().length === 0 || redeem.isPending}
                  className="w-full"
                >
                  {redeem.isPending ? "Unlocking…" : "Unlock"}
                </PillBtn>
              </div>
              {total > 0 && unlockedCount > 0 && (
                <p className="m-0 mt-6 text-center font-display text-lp-text-lt text-xs">
                  You have {unlockedCount} of {total} collection
                  {total === 1 ? "" : "s"} unlocked.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
